import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "@/assets/HR.png";
import FirIcon, { firName, firGroupName } from "@/components/FirIcon";
import ShellBanners from "@/components/ShellBanners";
import ThemePicker from "@/components/ThemePicker";
import useShell from "@/lib/useShell";

// Cheile (rutele) din bara de jos, în ordinea priorității: apar doar cele permise utilizatorului, cel mult patru, plus „Mai mult”.
const DOCK = ["/panou", "/planco/planning", "/admin/persons", "/mdm/employments", "/employee-files", "/notifications"];
const DOCK_MAX = 4;

/** Aspectul Fiord (implicit la instalările noi): antet simplu, conținutul pe tot ecranul și o bară de meniu plutitoare jos
 *  (patru elemente + „Mai mult”, care deschide tot meniul pe module). Aceleași pagini, aceleași drepturi ca în celelalte aspecte. */
export default function LayoutFiord({ children }) {
  const { user, nav, unread, platformVersion, pwa, iosHelp, setIosHelp, newBuild, licenseAlert, updateBanner, doLogout, roleLabel, groups } = useShell();
  const loc = useLocation();
  const [more, setMore] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  useEffect(() => { setMore(false); setUserOpen(false); }, [loc.pathname]);

  const all = groups.flatMap(g => g.items);
  // la Fiord nu există buton „Start”: Panoul de control (la „/” și la /panou) arată direct plăcile modulelor, apoi cifrele (App.js: FiordHome)
  const dockItems = DOCK.map(to => all.find(i => i.to === to)).filter(Boolean).slice(0, DOCK_MAX);
  const isCurrent = (to) => (to === "/panou" ? loc.pathname === "/" || loc.pathname === "/panou" : loc.pathname === to || loc.pathname.startsWith(to + "/"));
  const dockTo = (to) => (to === "/panou" ? "/" : to);

  return (
    <div className="fiord-shell" data-testid="fiord-shell">
      <header className="fiord-head">
        <Link to="/" className="fiord-brand" aria-label="Start" data-testid="sidebar-logo-link"><img src={logo} alt="ONOMAD" /><span className="name">Platforma de HR</span></Link>
        <span style={{ flex: 1 }} />
        <button type="button" className="fiord-head-btn" aria-label="Contul meu" aria-expanded={userOpen} onClick={() => setUserOpen(o => !o)} data-testid="user-menu">
          <FirIcon name="user" size={24} />{unread > 0 && <span className="fiord-dot" data-testid="user-dot" />}
        </button>
      </header>
      {userOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 70 }} onClick={() => setUserOpen(false)} />
          <div className="user-pop" data-testid="user-pop">
            <div className="who" data-testid="current-user">{user?.name || user?.username}</div>
            <div className="roles">{roleLabel}</div>
            <button className="btn btn-secondary" onClick={() => nav("/my-account")} data-testid="menu-my-account"><FirIcon name="user" size={18} />Contul meu</button>
            <button className="btn btn-secondary" onClick={() => nav("/notifications")} data-testid="menu-notifications"><FirIcon name="notif" size={18} />Notificări{unread > 0 ? ` (${unread})` : ""}</button>
            {(pwa.canInstall || pwa.ios) && <button className="btn btn-secondary" data-testid="install-app" onClick={() => (pwa.canInstall ? pwa.install() : setIosHelp(v => !v))}>Instalează aplicația</button>}
            {iosHelp && <div style={{ fontSize: 12, opacity: .8, background: "var(--soft)", borderRadius: 8, padding: "8px 10px", marginBottom: 8, lineHeight: 1.5 }} data-testid="ios-help">Pe iPhone/iPad: apasă <b>Distribuie</b> din Safari, apoi <b>Adaugă pe ecranul principal</b>.</div>}
            <ThemePicker />
            <button className="btn btn-primary" onClick={doLogout} data-testid="logout-btn" style={{ marginBottom: 0 }}><FirIcon name="iesire" size={18} />Deconectare</button>
            {platformVersion && <div style={{ fontSize: 11, opacity: .6, textAlign: "center", marginTop: 8 }} data-testid="platform-version">Versiunea {platformVersion}</div>}
          </div>
        </>)}
      <ShellBanners licenseAlert={licenseAlert} newBuild={newBuild} updateBanner={updateBanner} />
      <main className="fiord-main"><div className="app-content">{children}</div></main>

      <nav className="fiord-dock" aria-label="Meniu" data-testid="app-sidebar">
        <div className="fiord-dock-bar">
          {dockItems.map(it => (
            <NavLink key={it.to} to={dockTo(it.to)} end className={"fiord-dock-item" + (isCurrent(it.to) ? " active" : "")} aria-current={isCurrent(it.to) ? "page" : undefined}
              data-testid={`dock-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}>
              <FirIcon name={firName(it.to)} /><span>{it.to === "/panou" ? "Panou" : it.label}</span>
              {it.to === "/notifications" && unread > 0 && <b className="fiord-badge" data-testid="notif-badge">{unread}</b>}
            </NavLink>))}
          <button type="button" className={"fiord-dock-item" + (more ? " active" : "")} aria-expanded={more} onClick={() => setMore(m => !m)} data-testid="menu-toggle">
            <FirIcon name={more ? "inchide" : "mai"} /><span>Mai mult</span>
          </button>
        </div>
      </nav>
      {more && (
        <div className="fiord-sheet-overlay" onClick={() => setMore(false)} data-testid="nav-overlay">
          <div className="fiord-sheet" onClick={e => e.stopPropagation()} data-testid="fiord-sheet">
            {groups.map(g => (
              <section key={g.title}>
                <h2><FirIcon name={firGroupName(g.title)} size={18} />{g.title.replace("PlanCo - ", "PlanCo — ")}</h2>
                {g.items.map(it => (
                  <NavLink key={it.to} to={it.to} end className={({ isActive }) => "fiord-sheet-link" + (isActive ? " active" : "")} data-testid={`sheet-${it.to.replace(/\//g, "-").replace(/^-/, "") || "start"}`}>
                    <FirIcon name={firName(it.to)} size={20} /><span>{it.label}</span>
                    {it.to === "/notifications" && unread > 0 && <b className="fiord-badge static">{unread}</b>}
                  </NavLink>))}
              </section>))}
          </div>
        </div>)}
    </div>
  );
}
